import React, { useEffect, useRef, useState } from "react";
import { uploadTicketDocuments, getTicketDocuments, ITicketDocument } from "../ticket-module/services/ticketService";

const MAX_TOTAL_SIZE = 30 * 1024 * 1024; // 30MB

interface Props {
  ticketId: string;
  disabled?: boolean;
  onDocCountChange?: (count: number) => void;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function TicketDocumentUpload({ ticketId, disabled, onDocCountChange }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [documents, setDocuments] = useState<ITicketDocument[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadDocuments = () => {
    getTicketDocuments(ticketId)
      .then((res) => {
        const docs = res.data ?? [];
        setDocuments(docs);
        onDocCountChange?.(docs.length);
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadDocuments();
  }, [ticketId]);

  const totalSize = files.reduce((acc, f) => acc + f.size, 0);

  const addFiles = (incoming: FileList | File[]) => {
    const list = Array.from(incoming);
    const merged = [...files, ...list].filter(
      (f, i, arr) => arr.findIndex((x) => x.name === f.name && x.size === f.size) === i
    );
    const total = merged.reduce((acc, f) => acc + f.size, 0);
    if (total > MAX_TOTAL_SIZE) {
      setError("Toplam dosya boyutu 30MB'ı aşamaz.");
      return;
    }
    setError("");
    setSuccess("");
    setFiles(merged);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setError("");
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    addFiles(e.dataTransfer.files);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    const pastedFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === "file") {
        const f = items[i].getAsFile();
        if (f) pastedFiles.push(f);
      }
    }
    if (pastedFiles.length) addFiles(pastedFiles);
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    setError("");
    setSuccess("");
    try {
      await uploadTicketDocuments(ticketId, files);
      setSuccess(`${files.length} dosya başarıyla yüklendi.`);
      setFiles([]);
      loadDocuments();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Dosya yüklenirken hata oluştu.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{
      background: "#fff", borderRadius: 16,
      border: "1.5px solid #e9ecef",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      padding: "16px",
    }}>
      <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#374151", marginBottom: 12, display: "flex", alignItems: "center", gap: 7 }}>
        <i className="bi bi-paperclip" style={{ color: "#6366f1" }}></i>
        Dosya Ekle
      </div>

      {/* Drop Zone */}
      <div
        tabIndex={0}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onPaste={handlePaste}
        onClick={() => !disabled && fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${isDragging ? "#6366f1" : "#d1d5db"}`,
          borderRadius: 12,
          background: isDragging ? "#f5f3ff" : "#f9fafb",
          padding: "20px 12px",
          textAlign: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "all 0.15s",
          outline: "none",
          userSelect: "none",
        }}
      >
        <i className="bi bi-cloud-upload" style={{ fontSize: "1.6rem", color: isDragging ? "#6366f1" : "#9ca3af" }}></i>
        <div style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 6, lineHeight: 1.6 }}>
          Dosyaları sürükle & bırak<br />
          <span style={{ color: "#6366f1", fontWeight: 600 }}>Dosya seç</span> veya <kbd style={{ fontSize: "0.72rem", background: "#e5e7eb", padding: "1px 5px", borderRadius: 4 }}>Ctrl+V</kbd> ile yapıştır
        </div>
        <div style={{ fontSize: "0.7rem", color: "#9ca3af", marginTop: 4 }}>
          Maksimum toplam: 30MB
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ""; }}
          disabled={disabled}
        />
      </div>

      {/* Dosya listesi */}
      {files.length > 0 && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          {files.map((f, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "#f8f9fa", borderRadius: 8, padding: "6px 10px",
              border: "1px solid #e9ecef",
            }}>
              <i className="bi bi-file-earmark" style={{ color: "#6366f1", fontSize: "0.9rem", flexShrink: 0 }}></i>
              <span style={{ fontSize: "0.78rem", color: "#374151", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.name}
              </span>
              <span style={{ fontSize: "0.72rem", color: "#9ca3af", flexShrink: 0 }}>{formatSize(f.size)}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", padding: 0, fontSize: "0.85rem", flexShrink: 0 }}
              >
                <i className="bi bi-x-circle-fill"></i>
              </button>
            </div>
          ))}
          <div style={{ fontSize: "0.72rem", color: "#6b7280", textAlign: "right" }}>
            Toplam: {formatSize(totalSize)} / 30MB
          </div>
        </div>
      )}

      {/* Error / Success */}
      {error && (
        <div style={{ marginTop: 8, fontSize: "0.78rem", color: "#dc2626", display: "flex", alignItems: "center", gap: 5 }}>
          <i className="bi bi-exclamation-triangle-fill"></i> {error}
        </div>
      )}
      {success && (
        <div style={{ marginTop: 8, fontSize: "0.78rem", color: "#16a34a", display: "flex", alignItems: "center", gap: 5 }}>
          <i className="bi bi-check-circle-fill"></i> {success}
        </div>
      )}

      {/* Mevcut dökümanlar */}
      {documents.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Yüklü Dosyalar ({documents.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {documents.map((doc) => {
              const d = new Date(doc.uploadDate);
              const dateStr = `${String(d.getDate()).padStart(2,"0")}-${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()}`;
              return (
                <a
                  key={doc.id}
                  href={doc.documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: "#f8f9fa", borderRadius: 8, padding: "6px 10px",
                    border: "1px solid #e9ecef", textDecoration: "none",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#eef2ff")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#f8f9fa")}
                >
                  <i className="bi bi-file-earmark-arrow-down" style={{ color: "#6366f1", fontSize: "0.9rem", flexShrink: 0 }}></i>
                  <span style={{ fontSize: "0.78rem", color: "#374151", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {doc.name}
                  </span>
                  <span style={{ fontSize: "0.7rem", color: "#9ca3af", flexShrink: 0 }}>{dateStr}</span>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Yükle butonu */}
      {files.length > 0 && (
        <button
          type="button"
          onClick={handleUpload}
          disabled={uploading || disabled}
          style={{
            marginTop: 10, width: "100%", padding: "9px",
            borderRadius: 10, border: "none",
            background: uploading ? "#a5b4fc" : "linear-gradient(135deg, #6366f1, #4f46e5)",
            color: "#fff", fontWeight: 700, fontSize: "0.83rem",
            cursor: uploading ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
          }}
        >
          {uploading
            ? <><span className="spinner-border spinner-border-sm" />&nbsp;Yükleniyor...</>
            : <><i className="bi bi-cloud-upload-fill"></i> {files.length} Dosyayı Yükle</>
          }
        </button>
      )}
    </div>
  );
}
